<template>
  <div
    class="message-header"
    style="display: flex; gap: 4px; align-items: baseline; width: fit-content; position: relative"
  >
    <a
      ref="usernameRef"
      :href="userProfileHref"
      class="user-name"
      style="text-decoration: none; cursor: pointer"
      @mouseenter="showPopup"
      @mouseleave="hidePopup"
      @click.prevent="openProfileSlideOut"
    >
      <span style="font-weight: 900" class="author-name">
        {{ authorName }}
      </span>
    </a>
    <span
      class="timestamp"
    >
      {{ createdAt }}
    </span>
  </div>
  <!-- User Popup -->
  <teleport to="body">
    <div
      v-if="showUserPopup"
      ref="userPopup"
      class="user-popup"
      style="
          display: flex;
          gap: 0;
          height: fit-content;
          align-items: center;
          position: fixed;
          flex-direction: column;
          width: 300px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          border: 1px solid #e0e0e0;
        "
      @mouseenter="showPopup"
      @mouseleave="hidePopup"
    >
      <div
        class="popup-header"
        style="
            width: 100%;
            padding-left: 12px;
            padding-right: 12px;
            padding-bottom: 12px;
            padding-top: 12px;
            border-bottom: 1px solid #e8e8e8;
            box-sizing: border-box;
          "
      >
        <div
          class="popup-user-info"
          style="
              display: flex;
              gap: 12px;
              align-items: center;
              width: fit-content;
              position: relative
            "
        >
          <img
            :src="authorAvatar"
            alt="User avatar"
            class="popup-avatar"
            style="
                width: 40px;
                height: 40px;
                border-radius: 8px;
                border: 2px solid #f0f0f0;
              "
          >
          <div
            class="popup-user-details"
            style="
                display: flex;
                flex-direction: column;
                justify-content: center;
                width: fit-content;
                position: relative;
              "
          >
            <div
              class="popup-user-details-title-container"
              style="
                display: flex;
                align-items: center;
                gap: 4px;
                width: fit-content;
                position: relative;
              "
            >
              <span
                style="
                font-size: 16px;
                font-weight: 600;
              "
              >{{ authorName }}</span>
              <div class="status" :class="onlineStatusClass" />
            </div>
            <div style="height: 2px;" />
            <div class="joined-date" style="color: #616061; font-size: 12px;">
              Joined {{ formattedTimestamp }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="popup-actions"
        style="
            width: 100%;
            padding-left: 12px;
            padding-top: 12px;
            padding-bottom: 12px;
            padding-right: 12px;
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            gap: 8px;
            justify-content: center;
            box-sizing: border-box;
          "
      >
        <button
          type="button"
          class="popup-button button-secondary"
          style="
              padding: 8px 12px;
              border: 1px solid #ccc;
              background: white;
              border-radius: 8px;
              font-size: 14px;
              cursor: pointer;
              text-align: center;
              display: flex;
              width: 100%;
              align-items: center;
              gap: 8px;
              justify-content: center;
              text-decoration: none;
              color: #1d1c1d;
            "
          @click.prevent="openProfileSlideOut"
        >
          <svg
            width="18"
            height="18"
            class="button-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            />
          </svg>
          View Profile
        </button>
        <a
          :href="userDirectMessageHref"
          class="popup-button primary-button"
          style="
              padding: 8px 12px;
              border: 1px solid #ccc;
              border-radius: 8px;
              font-size: 14px;
              cursor: pointer;
              text-align: center;
              display: flex;
              width: 100%;
              align-items: center;
              gap: 8px;
              justify-content: center;
              text-decoration: none;
              color: white;
            "
          @click="hidePopup"
        >
          <svg
            width="18"
            height="18"
            class="button-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
            />
          </svg>
          Message
        </a>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import { useGeneralStore } from "%/stores/general-store";
import dayjs from "dayjs";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";
import { useConversationStore } from "%/apps/conversations/stores/conversation-store";
import { getUserProfileHref, getUserDirectMessageHref } from "%/apps/conversations/utils/user-link-utils";

export default {
  name: "MessageHeader",
  props: {
    messageId: {
      type: Number,
      required: true,
    },
  },
  emits: ["open:profile"],
  data() {
    return {
      showUserPopup: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["userData"]),
    ...mapState(useConversationStore, ["authorStatuses"]),
    message() {
      return this.getMessage(this.messageId);
    },
    createdAt() {
      return dayjs.unix(this.message.timestampCreated).format("hh:mm A");
    },
    authorName() {
      if (this.isAuthor) {
        return useGeneralStore().userData.first_name;
      }
      return this.message.authorName;
    },
    authorId() {
      return this.message.authorId || this.message.userId;
    },
    isAuthor() {
      return this.isMessageAuthor(this.messageId);
    },
    authorAvatar() {
      if (this.isAuthor) {
        return this.userData.avatar_url;
      }
      return this.message.avatarUrl || DEFAULT_USER_AVATAR;
    },
    userProfileHref() {
      return getUserProfileHref(this.authorId);
    },
    userDirectMessageHref() {
      return getUserDirectMessageHref(this.authorId);
    },
    formattedTimestamp() {
      return dayjs.unix(this.message.timestampCreated).format("DD MMMM YYYY");
    },
    onlineStatusClass() {
      if (this.authorStatuses[this.authorId]) {
        return "online";
      }
      return "offline";
    },
  },
  methods: {
    ...mapActions(useMessageStore, ["getMessage", "isMessageAuthor"]),
    openProfileSlideOut() {
      this.hidePopup();
      this.$emit("open:profile", this.authorId);
    },
    showPopup() {
      this.showUserPopup = true;
      this.$nextTick(() => {
        this.positionPopup();
      });
    },
    hidePopup() {
      this.showUserPopup = false;
    },
    positionPopup() {
      const usernameRect = this.$refs.usernameRef.getBoundingClientRect();
      const popup = this.$refs.userPopup;
      const popupRect = popup.getBoundingClientRect();

      let top = usernameRect.height + usernameRect.top - 2;

      if (window.innerHeight - usernameRect.top < 300) {
        const popupOffset = popupRect.height * -1;
        top = usernameRect.top + popupOffset;
      }

      popup.style.left = `${usernameRect.left}px`;
      popup.style.top = `${top}px`;
    },
  },
  styleGuide: () => ({
    timestampColor: { "color.font": "subtle" },
    authorNameColor: { "color.font": "standard" },
    primaryButtonBackground: { "color.background": "darker" },
    secondaryButtonHoverBackgroundColor: { "color.background": "balanced" },

    onlineColor: { "color.font": "success" },
    statusBorderColor: { "color.border": "dark" },
    statusBorderWidth: { "size.border": "thin" },
  }),
};
</script>

<style lang="scss" scoped>
.user-name {
  padding: 0 !important;
}

.author-name {
  color: v-bind("$getStyles.authorNameColor");
}

.user-name:hover .author-name {
  text-decoration: underline;
}

.timestamp, .timestamp:hover {
  color: v-bind("$getStyles.timestampColor");
}

.timestamp {
  text-decoration: none;
  font-size: 12px;
  color: #6b7280;
}

.timestamp:hover {
  text-decoration: underline;
}

.user-popup {
  z-index: 99999;
}

.primary-button {
  background-color: v-bind("$getStyles.primaryButtonBackground");
}

.button-secondary:hover {
  background-color: v-bind("$getStyles.secondaryButtonHoverBackground") !important;
}

.status {
  border-radius: 50%;
  width: 6px;
  height: 6px;

  &.online {
    border: v-bind("$getStyles.statusBorderWidth") solid v-bind("$getStyles.onlineColor") !important;
    background-color: v-bind("$getStyles.onlineColor") !important;
  }

  &.offline {
    border: v-bind("$getStyles.statusBorderWidth") solid v-bind("$getStyles.statusBorderColor") !important;
  }
}
</style>
